import React, { useState, useEffect } from 'react';
import '@/App.css';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { CourseProvider, useCourse } from '@/contexts/CourseContext';
import { courseAPI } from '@/services/api';
import ProgressBar from '@/components/ProgressBar';
import ChatPanelEmbedded from '@/components/ChatPanelEmbedded';
import Step1Metadata from '@/pages/Step1Metadata';
import Step2LearningObjectives from '@/pages/Step2LearningObjectives';
import Step3Activities from '@/pages/Step3Activities';
import Step4Finish from '@/pages/Step4Finish';
import LandingChoice from '@/pages/LandingChoice';
import ImportCourse from '@/pages/ImportCourse';
import Login from '@/pages/Login';
import { Toaster } from '@/components/ui/toaster';
import { ArrowLeft, LogOut, GripVertical } from 'lucide-react';
import { STORAGE_KEYS } from '@/constants';
import { getStepHeaderInfo } from '@/lib/stepHeaders';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';

// localStorage helper for Step 1 preview persistence (shared with Step1Metadata.js)
const PREVIEW_STORAGE_KEY = 'step1_preview_shown_';
function getPreviewShown(courseId) {
  if (!courseId) return false;
  try {
    return localStorage.getItem(PREVIEW_STORAGE_KEY + courseId) === 'true';
  } catch {
    return false;
  }
}

function AppContent() {
  const { t } = useTranslation();
  const { user, logout, loading: authLoading, isAuthenticated } = useAuth();
  const { currentStep, dispatch, resetCourse, course, importMode } = useCourse();
  const [appMode, setAppMode] = useState('choice'); // 'choice', 'create', 'import'
  const [selectedChatLanguage, setSelectedChatLanguage] = useState('English'); // Chat language for both modes

  // Debug logging
  useEffect(() => {
    console.log('AppContent render - user:', user, 'isAuthenticated:', isAuthenticated, 'authLoading:', authLoading);
  }, [user, isAuthenticated, authLoading]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-100 via-pink-50 to-teal-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user && !isAuthenticated) {
    console.log('Rendering Login component - no user or authentication');
    return <Login />;
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Metadata />;
      case 2:
        return <Step2LearningObjectives />;
      case 3:
        return <Step3Activities />;
      case 4:
        return <Step4Finish />;
      default:
        return <Step1Metadata />;
    }
  };


  const showChatPanel = currentStep >= 1 && currentStep <= 3;

  // Determine when to show split layout (preview on left, chat on right)
  // Once split layout is shown, it persists via localStorage (even if user deletes a field)
  const showSplitLayout = currentStep === 1 && course && (() => {
    // Check localStorage first - if preview was ever shown, keep split layout
    if (getPreviewShown(course.id)) {
      return true;
    }

    if (importMode) {
      // In import mode, show split view as soon as ANY field is filled (agent started working)
      return course.title ||
        (course.totalHours && course.totalHours > 0) ||
        course.educationalLevel ||
        course.grade ||
        course.subjectArea ||
        (course.description && course.description.trim() !== '');
    } else {
      // In regular mode, require ALL fields to be complete
      return course.title &&
        course.totalHours > 0 &&
        course.educationalLevel &&
        course.grade &&
        course.subjectArea &&
        course.description &&
        course.description.trim() !== '';
    }
  })();

  // Handle starting from scratch - clear old course data
  const handleStartFromScratch = async (language = 'English') => {
    // Reset course context completely with the selected language
    // Both interactionLanguage (chat) and language (content) start the same
    // User can change content language in Step 1 if they want different languages
    await resetCourse({
      language: language,
      interactionLanguage: language,
    });
    setAppMode('create');
  };

  // Handle import with language selection
  const handleImport = (language = 'English') => {
    setSelectedChatLanguage(language);
    setAppMode('import');
  };

  // Landing choice view
  if (appMode === 'choice') {
    return (
      <>
        <LandingChoice
          onCreateNew={handleStartFromScratch}
          onImport={handleImport}
        />
        <Toaster />
      </>
    );
  }

  // Import course view
  if (appMode === 'import') {
    return (
      <>
        <ImportCourse
          onBack={() => setAppMode('choice')}
          chatLanguage={selectedChatLanguage}
        />
        <Toaster />
      </>
    );
  }

  // Original create from scratch view
  return (
    <div className="h-screen bg-gradient-to-br from-orange-100 via-pink-50 to-teal-100 py-6 px-6 overflow-hidden" data-testid="app-container">
      <div className="max-w-[1800px] mx-auto h-full flex flex-col">
        {/* Header */}
        <div className="mb-3 flex-shrink-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <button
                onClick={() => setAppMode('choice')}
                className="flex items-center text-gray-700 hover:text-gray-900 mb-2 font-medium transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t('common.backToHome')}
              </button>
              {(() => {
                const stepHeaderInfo = getStepHeaderInfo(currentStep, importMode, t);
                const IconComponent = stepHeaderInfo.icon;
                // Calculate stats for Step 4
                const totalGoals = currentStep === 4 ? (course?.learningObjectives?.length || 0) : 0;
                const totalTopics = currentStep === 4 ? (course?.topics?.length || 0) : 0;
                const totalActivities = currentStep === 4 ? ((course?.topics || []).reduce((sum, t) => sum + (t.activities?.length || 0), 0)) : 0;
                return (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <IconComponent className={stepHeaderInfo.iconColor} size={36} />
                      <h1 className="text-3xl font-extrabold bg-gradient-to-r from-orange-600 via-pink-600 to-teal-600 bg-clip-text text-transparent">
                        {stepHeaderInfo.title}
                      </h1>
                    </div>
                    {stepHeaderInfo.subtitle && (
                      <p className="text-gray-600 mt-1 text-sm mb-2">
                        {stepHeaderInfo.subtitle}
                      </p>
                    )}
                    {currentStep === 4 && (
                      <div className="flex items-center gap-2 mt-2 mb-2">
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold">{t('step4.learningObjectives')}: <span className="font-bold">{totalGoals}</span></span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold">{t('step4.topics')}: <span className="font-bold">{totalTopics}</span></span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold">{t('step4.activities')}: <span className="font-bold">{totalActivities}</span></span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="flex items-start gap-3">
              <div className="text-right mr-2">
                <p className="text-sm text-gray-600">{t('common.loggedInAs')}</p>
                <p className="text-sm font-semibold text-gray-800">{user.username}</p>
              </div>
              <button
                onClick={logout}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                title={t('common.logout')}
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('common.logout')}
              </button>
            </div>
          </div>
          {/* Progress Bar in its own centered row */}
          <ProgressBar />
        </div>

        {/* Step Content with Chat Panel */}
        <div className="flex-1 min-h-0">
        {currentStep === 4 ? (
          <div className="h-full overflow-y-auto">
            {renderStep()}
          </div>
        ) : currentStep === 1 && !showSplitLayout ? (
          <div className="flex flex-col items-center h-full">
            <div className="w-full max-w-4xl h-full flex flex-col">
              <div className="flex-shrink-0 overflow-y-auto pb-2">
                {renderStep()}
              </div>
              <div className="flex-1 min-h-[500px] mt-2 mb-4">
                <ChatPanelEmbedded />
              </div>
            </div>
          </div>
        ) : (
          <PanelGroup direction="horizontal" className="h-full">
            <Panel defaultSize={50} minSize={25}>
              <div className="h-full overflow-y-auto pr-[1px]">
                {renderStep()}
              </div>
            </Panel>
            {showChatPanel && (
              <>
                <PanelResizeHandle className="w-4 mx-1 flex items-center justify-center group cursor-col-resize">
                  <div className="w-1.5 h-20 bg-gray-300 rounded-full group-hover:bg-orange-400 group-active:bg-orange-500 transition-colors flex items-center justify-center">
                    <GripVertical className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" />
                  </div>
                </PanelResizeHandle>
                <Panel defaultSize={50} minSize={30} className="h-full">
                  <ChatPanelEmbedded />
                </Panel>
              </>
            )}
          </PanelGroup>
        )}
        </div>

        <Toaster />
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <CourseProvider>
        <AppContent />
      </CourseProvider>
    </AuthProvider>
  );
}

export default App;